import { z } from "zod";
import { circuit, type Circuit } from "../circuit/circuit.js";
import {
  installBreaker,
  type BreakerInstallation,
} from "../circuit/installation.js";
import type { LegAssignment } from "../circuit/leg.js";
import type { PhotoRef } from "../circuit/photo.js";
import type { CircuitStatus } from "../circuit/status.js";
import type { WireMaterial } from "../circuit/wire.js";
import {
  decodeBreakerPlacement,
  encodeBreakerPlacement,
} from "./panel.js";
import { CodecError } from "./envelope.js";

/**
 * Phase 6a codec for the circuit-side aggregates: `Circuit`, `PhotoRef`,
 * `BreakerInstallation`. Determinism: key order matches the value-type
 * declaration; undefined optionals (legendTextOverride, notes,
 * wireGauge, wireMaterial, leg, feedsSubPanel) are omitted; collection
 * fields (`tags`, `photos`, `circuits`) preserve domain order. Read
 * dispatches through `circuit()` and `installBreaker()` so the duplicate
 * id and pole/circuit-count invariants are re-checked.
 */

const circuitStatusSchema: z.ZodType<CircuitStatus> = z.enum([
  "in-service",
  "spare",
  "disconnected",
]);

const wireMaterialSchema: z.ZodType<WireMaterial> = z.enum([
  "copper",
  "aluminum",
  "copper-clad-aluminum",
]);

const legAssignmentSchema: z.ZodType<LegAssignment> = z.enum([
  "A",
  "B",
  "C",
  "AB",
  "BC",
  "CA",
  "ABC",
]);

const photoRefJsonSchema = z.object({
  id: z.string().min(1),
});

export type PhotoRefJson = z.infer<typeof photoRefJsonSchema>;

function encodePhotoRef(p: PhotoRef): PhotoRefJson {
  return { id: p.id };
}

function decodePhotoRef(j: PhotoRefJson): PhotoRef {
  return { id: j.id };
}

export const circuitJsonSchema = z.object({
  id: z.string().min(1),
  shortDescription: z.string(),
  amperage: z.number().int().positive(),
  legendTextOverride: z.string().optional(),
  notes: z.string().optional(),
  status: circuitStatusSchema,
  wireGauge: z.string().optional(),
  wireMaterial: wireMaterialSchema.optional(),
  leg: legAssignmentSchema.optional(),
  tags: z.array(z.string()),
  criticalLoad: z.boolean(),
  photos: z.array(photoRefJsonSchema),
  feedsSubPanel: z.string().min(1).optional(),
});

export type CircuitJson = z.infer<typeof circuitJsonSchema>;

export function encodeCircuit(c: Circuit): Record<string, unknown> {
  const out: Record<string, unknown> = {
    id: c.id,
    shortDescription: c.shortDescription,
    amperage: c.amperage,
  };
  if (c.legendTextOverride !== undefined) {
    out["legendTextOverride"] = c.legendTextOverride;
  }
  if (c.notes !== undefined) out["notes"] = c.notes;
  out["status"] = c.status;
  if (c.wireGauge !== undefined) out["wireGauge"] = c.wireGauge;
  if (c.wireMaterial !== undefined) out["wireMaterial"] = c.wireMaterial;
  if (c.leg !== undefined) out["leg"] = c.leg;
  out["tags"] = [...c.tags];
  out["criticalLoad"] = c.criticalLoad;
  out["photos"] = c.photos.map(encodePhotoRef);
  if (c.feedsSubPanel !== undefined) out["feedsSubPanel"] = c.feedsSubPanel;
  return out;
}

export function decodeCircuit(input: unknown): Circuit {
  const parsed = circuitJsonSchema.safeParse(input);
  if (!parsed.success) {
    throw new CodecError(
      "INVALID_PAYLOAD",
      `decodeCircuit: ${parsed.error.message}`,
    );
  }
  const j = parsed.data;
  return circuit({
    id: j.id,
    shortDescription: j.shortDescription,
    amperage: j.amperage,
    ...(j.legendTextOverride !== undefined
      ? { legendTextOverride: j.legendTextOverride }
      : {}),
    ...(j.notes !== undefined ? { notes: j.notes } : {}),
    status: j.status,
    ...(j.wireGauge !== undefined ? { wireGauge: j.wireGauge } : {}),
    ...(j.wireMaterial !== undefined ? { wireMaterial: j.wireMaterial } : {}),
    ...(j.leg !== undefined ? { leg: j.leg } : {}),
    tags: j.tags,
    criticalLoad: j.criticalLoad,
    photos: j.photos.map(decodePhotoRef),
    ...(j.feedsSubPanel !== undefined ? { feedsSubPanel: j.feedsSubPanel } : {}),
  });
}

export const breakerInstallationJsonSchema = z.object({
  placement: z.unknown(),
  circuits: z.array(z.unknown()),
});

export type BreakerInstallationJson = z.infer<
  typeof breakerInstallationJsonSchema
>;

export function encodeBreakerInstallation(
  i: BreakerInstallation,
): Record<string, unknown> {
  return {
    placement: encodeBreakerPlacement(i.placement),
    circuits: i.circuits.map(encodeCircuit),
  };
}

export function decodeBreakerInstallation(input: unknown): BreakerInstallation {
  const parsed = breakerInstallationJsonSchema.safeParse(input);
  if (!parsed.success) {
    throw new CodecError(
      "INVALID_PAYLOAD",
      `decodeBreakerInstallation: ${parsed.error.message}`,
    );
  }
  const j = parsed.data;
  const placement = decodeBreakerPlacement(j.placement);
  const circuits = j.circuits.map(decodeCircuit);
  return installBreaker(placement, circuits);
}
